/**
 * Formulário com o nível de acesso de uma pessoa em cada módulo.
 *
 * Papel: desenha uma linha por módulo de `MODULOS_CONCEDIVEIS`, cada uma com as
 * opções sem acesso, ver e editar, e grava o conjunto inteiro de uma vez por
 * `useSalvarPermissoes`.
 *
 * Como usar: quem monta a grade deverá passar `key={usuario.id}`. O formulário
 * guarda os valores iniciais na primeira montagem; trocar de pessoa sem trocar a
 * `key` manteria na tela a grade da pessoa anterior, e salvar gravaria aquela
 * grade na pessoa nova.
 *
 * A grade não sabe se a pessoa é administradora — o papel está no token dela, e
 * não na listagem —, e por isso avisa no topo que a grade não tem efeito sobre
 * quem tem o papel. Revogar tudo aqui de um administrador não lhe tira nada.
 */
import {
  type DefinirPermissoes,
  MODULOS_CONCEDIVEIS,
  type Permissao,
  ROTULOS_MODULO,
  type Usuario,
  definirPermissoesSchema,
} from '@concierge/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useId } from 'react';
import { useForm, useWatch } from 'react-hook-form';

import { ErroDaApi } from '@/shared/api';
import { Button } from '@/shared/ui/button';
import { DialogFooter } from '@/shared/ui/dialog';
import { Label } from '@/shared/ui/label';
import { RadioGroup, RadioGroupItem } from '@/shared/ui/radio-group';
import { useSalvarPermissoes } from '../hooks/usar-usuarios';
import { type NivelNaGrade } from '../types';

/** As opções de cada linha, na ordem em que aparecem. */
const OPCOES: readonly { valor: NivelNaGrade; rotulo: string }[] = [
  { valor: 'nenhum', rotulo: 'Sem acesso' },
  { valor: 'ver', rotulo: 'Ver' },
  { valor: 'editar', rotulo: 'Editar' },
];

/**
 * Diz se um texto do grupo de opções é um dos níveis da grade.
 *
 * O `onValueChange` do grupo entrega `string`, e é este teste que permite
 * estreitar o tipo sem conversão forçada.
 *
 * @param valor - O que o grupo de opções entregou.
 * @returns Verdadeiro para `nenhum`, `ver` ou `editar`.
 */
function ehNivelNaGrade(valor: string): valor is NivelNaGrade {
  return OPCOES.some((opcao) => opcao.valor === valor);
}

/**
 * Troca o nível de um módulo dentro do conjunto de permissões.
 *
 * `nenhum` tira o módulo da lista, em vez de gravá-lo com um nível que o
 * contrato não aceita. A ordem de `MODULOS_CONCEDIVEIS` é mantida, para o pedido
 * sair igual toda vez que a grade for a mesma.
 *
 * @param atuais - Conjunto antes da mudança.
 * @param modulo - Módulo cuja linha mudou.
 * @param nivel - Nível marcado na linha.
 * @returns O conjunto novo, sem repetição.
 */
function trocarNivel(
  atuais: readonly Permissao[],
  modulo: Permissao['modulo'],
  nivel: NivelNaGrade,
): Permissao[] {
  const outras = atuais.filter((permissao) => permissao.modulo !== modulo);
  const novas = nivel === 'nenhum' ? outras : [...outras, { modulo, nivel }];
  return novas.sort(
    (a, b) =>
      MODULOS_CONCEDIVEIS.findIndex((chave) => chave === a.modulo) -
      MODULOS_CONCEDIVEIS.findIndex((chave) => chave === b.modulo),
  );
}

interface GradePermissoesProps {
  usuario: Usuario;
  onSalvo: () => void;
  onCancelar: () => void;
}

/**
 * Grade de permissões de uma pessoa, com salvar e cancelar.
 *
 * Salvar fica desabilitado enquanto nada muda e enquanto a gravação não
 * responde; o segundo caso é o que impede o clique duplo de mandar dois pedidos.
 * Recusa da API aparece acima dos botões, com a mensagem que ela devolveu.
 *
 * @param usuario - A pessoa cuja grade é editada, como veio da listagem.
 * @param onSalvo - Chamado depois de a API confirmar a gravação, normalmente
 *   para fechar a caixa.
 * @param onCancelar - Chamado ao desistir, sem gravar nada.
 * @returns O formulário da grade.
 */
export function GradePermissoes({ usuario, onSalvo, onCancelar }: GradePermissoesProps) {
  const prefixo = useId();
  const salvar = useSalvarPermissoes();

  const form = useForm<DefinirPermissoes>({
    resolver: zodResolver(definirPermissoesSchema),
    defaultValues: {
      // `usuarios` não é concedível; uma linha antiga assim seria recusada ao
      // salvar e travaria a grade inteira da pessoa.
      permissoes: usuario.permissoes.filter((permissao) => permissao.modulo !== 'usuarios'),
    },
  });

  const permissoes = useWatch({ control: form.control, name: 'permissoes' });
  const { errors, isDirty, isSubmitting } = form.formState;
  const mensagemDeErro = errors.root?.servidor?.message ?? errors.permissoes?.message;

  /**
   * Grava o conjunto validado e avisa quem montou a grade.
   *
   * A recusa vira erro do formulário, e não exceção solta: sem o `catch`, a
   * rejeição do `mutateAsync` sairia do `handleSubmit` sem mensagem na tela.
   */
  async function handleSalvar(dados: DefinirPermissoes) {
    try {
      await salvar.mutateAsync({ id: usuario.id, dados });
      onSalvo();
    } catch (erro: unknown) {
      form.setError('root.servidor', {
        message:
          erro instanceof ErroDaApi
            ? erro.message
            : 'Não foi possível salvar as permissões. Tente de novo.',
      });
    }
  }

  return (
    <form onSubmit={form.handleSubmit(handleSalvar)}>
      <div className="space-y-1">
        <p className="mb-4 rounded-md bg-secondary p-3 text-sm text-muted-foreground">
          Quem tem o papel de administrador no Auth0 já acessa todos os módulos, e esta grade
          não muda nada para essa pessoa.
        </p>

        {MODULOS_CONCEDIVEIS.map((modulo) => {
          const idDoRotulo = `${prefixo}-${modulo}`;
          const atual: NivelNaGrade =
            permissoes.find((permissao) => permissao.modulo === modulo)?.nivel ?? 'nenhum';

          return (
            <div
              key={modulo}
              className="flex flex-col gap-2 border-b border-border py-3 last:border-b-0"
            >
              <span id={idDoRotulo} className="text-sm font-medium text-foreground">
                {ROTULOS_MODULO[modulo]}
              </span>
              <RadioGroup
                aria-labelledby={idDoRotulo}
                value={atual}
                onValueChange={(valor) => {
                  if (!ehNivelNaGrade(valor)) return;
                  form.setValue('permissoes', trocarNivel(permissoes, modulo, valor), {
                    shouldDirty: true,
                  });
                }}
                className="flex flex-wrap gap-x-5 gap-y-2"
              >
                {OPCOES.map((opcao) => {
                  const idDaOpcao = `${idDoRotulo}-${opcao.valor}`;
                  return (
                    <div key={opcao.valor} className="flex items-center gap-2">
                      <RadioGroupItem value={opcao.valor} id={idDaOpcao} />
                      <Label htmlFor={idDaOpcao} className="font-normal">
                        {opcao.rotulo}
                      </Label>
                    </div>
                  );
                })}
              </RadioGroup>
            </div>
          );
        })}
      </div>

      {mensagemDeErro !== undefined && (
        <p role="alert" className="mt-4 text-sm text-perigo">
          {mensagemDeErro}
        </p>
      )}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancelar} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="submit" disabled={!isDirty || isSubmitting}>
          {isSubmitting ? 'Salvando…' : 'Salvar permissões'}
        </Button>
      </DialogFooter>
    </form>
  );
}
